import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../api/client";

type Activity = {
  id: string;
  activityType: string;
  config?: Record<string, unknown>;
  questions?: Array<{ id: string; text: string; options: Array<{ _id: string; text: string; isCorrect?: boolean }> }>;
};

type LessonData = {
  id: string;
  title: string;
  description: string;
  unlocked: boolean;
  completed: boolean;
  hasLessonQuiz?: boolean;
  passingScore?: number;
  quizPassed?: boolean;
  submissionStatus?: "none" | "pending" | "graded";
  submissionPassed?: boolean | null;
  videoUrl: string | null;
  activities: Activity[];
};

type QuizQuestion = {
  id: string;
  questionType: "multipleChoice" | "openEnded";
  question: string;
  options: string[];
};

type QuizData = {
  quizId: string;
  title: string;
  questions: QuizQuestion[];
};

export function LessonPlayer() {
  const { slug, lessonId } = useParams<{ slug: string; lessonId: string }>();
  const [lesson, setLesson] = useState<LessonData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quiz, setQuiz] = useState<QuizData | null>(null);

  useEffect(() => {
    if (!slug || !lessonId) return;
    api
      .get(`/courses/${slug}/lessons/${lessonId}`)
      .then((res) => {
        if (res.data.success) setLesson(res.data.data);
      })
      .catch(() => setError("Could not load lesson."))
      .finally(() => setLoading(false));

    api
      .get(`/courses/${slug}/lessons/${lessonId}/quiz`)
      .then((res) => {
        if (res.data.success && res.data.data) setQuiz(res.data.data);
      })
      .catch(() => {});
  }, [slug, lessonId]);

  const markComplete = () => {
    if (!slug || !lessonId) return;
    api
      .post(`/courses/${slug}/lessons/${lessonId}/complete`)
      .then(() => {
        setLesson((prev) => (prev ? { ...prev, completed: true } : null));
      })
      .catch(() => {});
  };

  if (loading) {
    return (
      <div className="min-h-full bg-stone/40">
        <div className="max-w-3xl mx-auto px-4 py-16">
          <div className="animate-pulse space-y-6">
            <div className="h-4 w-40 bg-gray-200 rounded" />
            <div className="h-8 w-3/4 bg-gray-200 rounded" />
            <div className="aspect-video bg-gray-100 rounded-2xl" />
            <div className="h-32 bg-gray-100 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }
  if (error || !lesson) {
    return (
      <div className="min-h-full bg-stone/40 flex items-center justify-center px-4 py-16">
        <div className="rounded-2xl border border-red-200 bg-white p-10 text-center shadow-soft max-w-md">
          <div className="w-14 h-14 mx-auto rounded-full bg-red-100 flex items-center justify-center text-red-500">
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-red-600 font-medium mt-4">{error || "Not found."}</p>
          <Link to={`/dashboard/courses/${slug}`} className="mt-6 inline-block font-semibold text-gold-500 hover:text-gold-400 transition-colors">
            Back to course
          </Link>
        </div>
      </div>
    );
  }
  if (!lesson.unlocked) {
    return (
      <div className="min-h-full bg-stone/40 flex items-center justify-center px-4 py-16">
        <div className="rounded-2xl border border-amber-200 bg-white p-10 text-center shadow-soft max-w-md">
          <div className="w-14 h-14 mx-auto rounded-full bg-amber-100 flex items-center justify-center text-amber-600">
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <p className="text-amber-800 font-medium mt-4">Complete the previous lesson first.</p>
          <Link to={`/dashboard/courses/${slug}`} className="mt-6 inline-block font-semibold text-gold-500 hover:text-gold-400 transition-colors">
            Back to course
          </Link>
        </div>
      </div>
    );
  }

  const baseUrl = process.env.REACT_APP_API_URL?.replace("/api", "") || "";

  return (
    <div className="min-h-full bg-stone/40">
      <div className="bg-ink-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link
            to={`/dashboard/courses/${slug}`}
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-gold-300 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Back to course
          </Link>
          <div className="mt-6 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-serif text-2xl md:text-3xl font-semibold tracking-tight">
                {lesson.title}
              </h1>
              {lesson.description && (
                <p className="mt-2 text-slate-400 text-sm leading-relaxed max-w-xl">
                  {lesson.description}
                </p>
              )}
            </div>
            {lesson.completed && (
              <span className="inline-flex items-center gap-2 rounded-full bg-green-500/20 px-3 py-1.5 text-sm font-medium text-green-300 shrink-0">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Completed
              </span>
            )}
          </div>
        </div>
        <div className="h-px w-full bg-gradient-to-r from-transparent via-gold-400/30 to-transparent" aria-hidden />
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {lesson.videoUrl && (
          <div className="rounded-2xl overflow-hidden bg-ink-950 shadow-soft-lg ring-1 ring-black/10">
            <video
              src={baseUrl + lesson.videoUrl}
              controls
              className="w-full"
              onEnded={() => {
                if (!lesson.hasLessonQuiz) markComplete();
              }}
            />
          </div>
        )}

        {lesson.activities.map((act) => (
          <div
            key={act.id}
            className="mt-10 rounded-2xl border border-gray-200 bg-white p-6 md:p-8 shadow-soft"
          >
            {act.activityType === "QUIZ" && act.questions && (
              <QuizBlock
                slug={slug!}
                lessonId={lessonId!}
                activityId={act.id}
                questions={act.questions}
                onComplete={() => {
                  if (!lesson.hasLessonQuiz) markComplete();
                }}
              />
            )}
            {act.activityType === "SCENARIO" && act.config && (
              <ScenarioBlock
                slug={slug!}
                lessonId={lessonId!}
                activityId={act.id}
                config={act.config}
                onComplete={() => {
                  if (!lesson.hasLessonQuiz) markComplete();
                }}
              />
            )}
          </div>
        ))}

        {quiz && quiz.questions.length > 0 && (
          <div className="mt-10 rounded-2xl border border-gray-200 bg-white p-6 md:p-8 shadow-soft">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h3 className="font-serif text-xl font-semibold text-ink-950 tracking-tight">
                  {quiz.title || "Lesson Quiz"}
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  Take the lesson quiz on a dedicated page without video playback.
                </p>
              </div>
              <Link
                to={`/dashboard/courses/${slug}/lessons/${lessonId}/quiz`}
                className="inline-flex items-center gap-2 rounded-lg bg-gold-400 px-5 py-2.5 text-ink-950 font-semibold hover:bg-gold-300 transition-colors shrink-0"
              >
                Open quiz page
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
            </div>
          </div>
        )}

        {!lesson.videoUrl && lesson.activities.length === 0 && !quiz && (
          <div className="mt-10 rounded-2xl border border-gray-200 bg-white p-8 shadow-soft">
            <p className="text-gray-600 mb-6">Mark this lesson complete when you&apos;re done reviewing.</p>
            <button
              type="button"
              onClick={markComplete}
              disabled={lesson.completed}
              className="rounded-lg bg-gold-400 px-6 py-3.5 text-ink-950 font-semibold hover:bg-gold-300 transition-colors shadow-md disabled:opacity-50"
            >
              {lesson.completed ? "Completed" : "Mark complete"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ======================== ACTIVITY QUIZ BLOCK ======================== */

function QuizBlock({
  slug,
  lessonId,
  activityId,
  questions,
  onComplete,
}: {
  slug: string;
  lessonId: string;
  activityId: string;
  questions: Array<{ id: string; text: string; options: Array<{ _id: string; text: string }> }>;
  onComplete: () => void;
}) {
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState<{ correct: boolean } | null>(null);

  const handleSubmit = () => {
    const answers = Object.entries(selected).map(([questionId, optionId]) => ({ questionId, optionId }));
    api.post(`/courses/${slug}/lessons/${lessonId}/activity`, { activityId, answers }).then((res) => {
      setResult({ correct: res.data.correct });
      setSubmitted(true);
      if (res.data.correct) onComplete();
    });
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gold-400/10 text-gold-600">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </span>
        <h3 className="font-serif text-xl font-semibold text-ink-950 tracking-tight">Quiz</h3>
      </div>
      {questions.map((q) => (
        <div key={q.id} className="mb-8">
          <p className="font-medium text-ink-950">{q.text}</p>
          <div className="mt-3 space-y-2">
            {(q.options || []).map((opt) => (
              <label
                key={opt._id}
                className="flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3.5 cursor-pointer hover:border-gold-400/40 hover:bg-stone/30 transition-colors has-[:checked]:border-gold-400 has-[:checked]:bg-gold-50/50"
              >
                <input
                  type="radio"
                  name={q.id}
                  checked={selected[q.id] === opt._id}
                  onChange={() => setSelected((s) => ({ ...s, [q.id]: opt._id }))}
                  disabled={submitted}
                  className="text-gold-500 focus:ring-gold-400"
                />
                <span className="text-ink-950">{opt.text}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
      {!submitted && (
        <button
          type="button"
          onClick={handleSubmit}
          className="rounded-lg bg-gold-400 px-5 py-2.5 text-ink-950 font-semibold hover:bg-gold-300 transition-colors"
        >
          Submit
        </button>
      )}
      {result && (
        <div
          className={`mt-4 rounded-xl px-4 py-3 flex items-center gap-3 text-sm font-medium ${
            result.correct ? "bg-green-50 text-green-700 border border-green-200" : "bg-amber-50 text-amber-700 border border-amber-200"
          }`}
        >
          {result.correct ? (
            <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          )}
          {result.correct ? "Correct!" : "Not quite. Review and try again."}
        </div>
      )}
    </div>
  );
}

/* ======================== SCENARIO BLOCK ======================== */

function ScenarioBlock({
  slug,
  lessonId,
  activityId,
  config,
  onComplete,
}: {
  slug: string;
  lessonId: string;
  activityId: string;
  config: Record<string, unknown>;
  onComplete: () => void;
}) {
  const prompt = (config.prompt as string) || "";
  const choices = (config.choices as Array<{ id: string; text: string }>) || [];
  const [choiceId, setChoiceId] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState<{ correct: boolean; feedback?: string } | null>(null);

  const handleSubmit = () => {
    if (choiceId === null) return;
    api.post(`/courses/${slug}/lessons/${lessonId}/activity`, { activityId, selectedChoiceId: choiceId }).then((res) => {
      setResult({
        correct: res.data.correct,
        feedback: res.data.correct ? (config.feedbackCorrect as string) : (config.feedbackIncorrect as string),
      });
      setSubmitted(true);
      if (res.data.correct) onComplete();
    });
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gold-400/10 text-gold-600">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </span>
        <h3 className="font-serif text-xl font-semibold text-ink-950 tracking-tight">Scenario</h3>
      </div>
      <p className="text-gray-700 mb-6 leading-relaxed">{prompt}</p>
      <div className="space-y-2">
        {choices.map((c) => (
          <label
            key={c.id}
            className="flex items-center gap-3 rounded-xl border border-gray-200 px-4 py-3.5 cursor-pointer hover:border-gold-400/40 hover:bg-stone/30 transition-colors has-[:checked]:border-gold-400 has-[:checked]:bg-gold-50/50"
          >
            <input
              type="radio"
              name="scenario"
              checked={choiceId === c.id}
              onChange={() => setChoiceId(c.id)}
              disabled={submitted}
              className="text-gold-500 focus:ring-gold-400"
            />
            <span className="text-ink-950">{c.text}</span>
          </label>
        ))}
      </div>
      {!submitted && (
        <button
          type="button"
          onClick={handleSubmit}
          disabled={choiceId === null}
          className="mt-6 rounded-lg bg-gold-400 px-5 py-2.5 text-ink-950 font-semibold hover:bg-gold-300 disabled:opacity-50 transition-colors"
        >
          Submit
        </button>
      )}
      {result && (
        <div
          className={`mt-4 rounded-xl px-4 py-3 flex items-start gap-3 text-sm font-medium ${
            result.correct ? "bg-green-50 text-green-700 border border-green-200" : "bg-amber-50 text-amber-700 border border-amber-200"
          }`}
        >
          {result.correct ? (
            <svg className="w-5 h-5 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-5 h-5 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          )}
          <span>{result.feedback || (result.correct ? "Correct!" : "Not quite.")}</span>
        </div>
      )}
    </div>
  );
}
